import { LatestReferral, TransferEvent } from '@/services/api.types';

// The patient's CURRENT referral, derived from their transfer-event stream.
//
// This exists to answer one question the receiving tenant's detail header asks:
// may this patient be discharged out of the eHospital, and which referral does
// the discharge act on (PD-2941).
//
// ── why it is derived here and not read off the patient ──
//
// The legacy build had the answer served as two fields on the patient detail
// response, `allow_discharged` and `discharged_id`, and this repo carried a
// comment claiming the new contract does not serve them so the button had to
// stay hidden. That comment was wrong about what it would take. The legacy
// BACKEND computed those two fields like this
// (rpms-service .../logic/dashboard/patientcommonsuminfov2.go):
//
//     if tenant.Role == Sink:
//         latest := latest incoming referral of this patient
//         if latest.Status == Accept:
//             allow_discharged = true
//             discharged_id    = latest.Id
//
// — a tenant role and the state of one referral. Both are already in the
// browser: the role is the `sources` branch of the configuration oneof, and the
// referral history is GET /v1/patients/{patient}/transfer-events, which the
// detail page ALREADY fetches for the transfer-log tab. No new endpoint, no new
// request, and nothing for the backend to add.
//
// ── why the event stream can answer it ──
//
// A referral's life is Applied → (Accepted | Rejected) → Ended, one event per
// step, and every step is on both ends' streams (the applying tenant's and the
// deciding tenant's). So the last event of a referral IS its state; there is
// nothing to infer.
//
// The alternative was to take the id from here and then ask GET
// /v1/transfers/{id} for the authoritative status. Not taken: that is the
// INBOX-TICKET endpoint, whose permission surface differs from the detail
// page's, so a clinician holding only the log capability would collect a 403 on
// every patient. (Availability is not the reason to avoid it — a decided
// referral's GetTransfer does no live peer fetch, so it cannot 503.)

// `LatestReferral` is declared in api.types.ts, beside the response it rides on.

// Instants, not characters: the wire may mix UTC offsets, so lexical order on
// RFC3339 is not chronological order. Same reasoning as utils/transfer-ignore.
const instant = (event: TransferEvent): number => Date.parse(event.at ?? '');

/**
 * The patient's most recently CREATED referral, and the kind of its latest
 * event. `undefined` when the patient has no referral at all.
 */
export const latestReferralOf = (
  events: readonly TransferEvent[] | undefined
): LatestReferral | undefined => {
  const referralEvents = (events ?? []).filter(
    event =>
      // Consent events belong to no referral. The contract says they carry an
      // empty `transfer_id`, and `logType` says the same thing from the other
      // side — both are checked because either one drifting alone would let a
      // consent grant be read as an admission: `ConsentGranted` and `Accepted`
      // BOTH reduce to the bare kind 'accept', which is the value this function
      // reports.
      !!event.transferId &&
      event.logType !== 'share' &&
      // An event with no parsable timestamp cannot be ordered against the
      // others, and ordering is the whole job here.
      !Number.isNaN(instant(event))
  );

  if (!referralEvents.length) {
    return undefined;
  }

  const byReferral = new Map<string, TransferEvent[]>();
  referralEvents.forEach(event => {
    const bucket = byReferral.get(event.transferId as string);
    if (bucket) {
      bucket.push(event);
    } else {
      byReferral.set(event.transferId as string, [event]);
    }
  });

  const referrals = Array.from(byReferral, ([id, own]) => {
    const sorted = [...own].sort((a, b) => instant(a) - instant(b));
    return {
      id,
      lastEventKind: sorted[sorted.length - 1].kind ?? '',
      createdAt: sorted[0].at as string,
    };
  });

  // ⚠️ Newest by CREATION, not by last activity. The legacy backend took the
  // latest referral ROW, and the two orders come apart in exactly one shape: a
  // new referral is rejected, and only afterwards the older one is ended. Last
  // activity would then pick the OLD referral and offer a discharge on it.
  return referrals.reduce((newest, referral) =>
    Date.parse(referral.createdAt) > Date.parse(newest.createdAt)
      ? referral
      : newest
  );
};

/**
 * Whether this patient may be discharged out of the eHospital, and against
 * which referral — the legacy `allow_discharged` / `discharged_id` pair.
 *
 * Sink-side only: on a sending tenant the question does not arise, and the
 * legacy backend did not answer it there either.
 */
export const dischargeableReferral = (
  isSink: boolean,
  latest: LatestReferral | undefined
): LatestReferral | undefined =>
  isSink && latest?.lastEventKind === 'accept' ? latest : undefined;
